import { useI18n } from '@cascade/react/i18n';
import {
  Badge,
  Card,
  Markdown,
  Radio,
  Select,
  Text,
} from '@cascade/ui-library';
import type { TRadioProps, TSelectOptionProps } from '@cascade/ui-library';
import { useId } from 'react';
import {
  getOptionCardVariant,
  toggleOption,
  toOptionIds,
} from './SelectWidget.helpers.ts';
import classes from './SelectWidget.module.scss';
import type { TSelectWidgetProps } from './SelectWidget.types.ts';
import { SideStepButton } from '#views/experience/experience-step/experience-section/side-step-button/index.ts';

export const SelectWidget = ({
  widget,
  label,
  value,
  isDisabled,
  onChange,
}: TSelectWidgetProps) => {
  const options = widget.options ?? [];
  const isMultiple = Boolean(widget.multiple);
  const selectedOptionId = value.at(0) ?? '';

  const groupName = useId();
  const { translate } = useI18n();

  const choices = options.map((option): TSelectOptionProps => ({
    label: option.ai_recommended
      ? translate('experience.option.recommended', { title: option.title })
      : option.title,
    value: option.id,
    feedbackLevel: option.ai_recommended ? 'success' : undefined,
  }));

  const changeChoice = (choice: string | string[]) => {
    const optionIds = toOptionIds(choice);

    onChange(optionIds);
  };

  const selectOption = (optionId: string) => {
    const optionIds = toggleOption(value, optionId, isMultiple);

    onChange(optionIds);
  };

  if (options.length === 0) {
    return null;
  }

  if (widget.select_type === 'select') {
    return (
      <Select
        label={label}
        labelDisplay='hidden'
        options={choices}
        value={isMultiple ? value : selectedOptionId}
        multiple={isMultiple}
        disabled={isDisabled}
        onChange={changeChoice}
      />
    );
  }

  if (widget.select_type === 'radio') {
    return (
      <Radio
        label={label}
        labelDisplay='hidden'
        options={choices as TRadioProps['options']}
        value={selectedOptionId}
        disabled={isDisabled}
        onChange={changeChoice}
      />
    );
  }

  return (
    <div
      className={classes.cards}
      role={isMultiple ? 'group' : 'radiogroup'}
      aria-label={label}
    >
      {options.map((option) => {
        const isSelected = isMultiple
          ? value.includes(option.id)
          : option.id === selectedOptionId;

        const isRecommended = Boolean(option.ai_recommended);
        const isCardDisabled = isDisabled && !isSelected;
        const cardVariant = getOptionCardVariant(isSelected, isRecommended);

        return (
          <label key={option.id} className={classes.option}>
            <input
              className={classes.input}
              type={isMultiple ? 'checkbox' : 'radio'}
              name={groupName}
              aria-label={option.title}
              checked={isSelected}
              disabled={isDisabled}
              onChange={() => selectOption(option.id)}
            />
            <Card
              variant={cardVariant}
              fillVariant={Boolean(cardVariant)}
              selected={isSelected}
              disabled={isCardDisabled}
              padding='md'
              borderRadius='md'
            >
              <div className={classes.content}>
                <div className={classes.head}>
                  <div className={classes.text}>
                    <Text type='h6' weight='semibold' content={option.title} />
                    {option.description ? (
                      <Text
                        type='body-2'
                        weight='medium'
                        content={option.description}
                      />
                    ) : null}
                  </div>
                  <div className={classes.badges}>
                    {isRecommended ? (
                      <Badge
                        label={translate('experiences.recommended')}
                        iconType='sparkle'
                        variant='success-darker'
                        size='sm'
                      />
                    ) : null}
                    {option.info_badge ? (
                      <Badge
                        label={option.info_badge}
                        variant='information-darker'
                        size='sm'
                      />
                    ) : null}
                    {isSelected ? (
                      <Badge
                        label={translate('experience.option.selected')}
                        iconType='check'
                        variant='brand-darker'
                        size='sm'
                        iconOnly
                      />
                    ) : null}
                  </div>
                </div>
                {option.markdown ? (
                  <Markdown content={option.markdown} />
                ) : null}
                {option.side_step_button ? (
                  <div className={classes['side-step']}>
                    <SideStepButton button={option.side_step_button} />
                  </div>
                ) : null}
              </div>
            </Card>
          </label>
        );
      })}
    </div>
  );
};
